import React, { useMemo } from 'react';

// Purely decorative QR-looking grid. `seed` reshuffles the modules so it
// "regenerates" on demand. Not scannable — visual only.
const FakeQR: React.FC<{ seed: number; className?: string }> = ({
  seed,
  className = '',
}) => {
  const N = 21;
  const cells = useMemo(() => {
    // Simple seeded PRNG (mulberry32).
    let s = seed >>> 0;
    const rng = () => {
      s |= 0;
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    const inFinder = (r: number, c: number) => {
      const corners = [
        [0, 0],
        [0, N - 7],
        [N - 7, 0],
      ];
      return corners.some(
        ([fr, fc]) => r >= fr && r < fr + 7 && c >= fc && c < fc + 7,
      );
    };

    const grid: boolean[][] = [];
    for (let r = 0; r < N; r++) {
      const row: boolean[] = [];
      for (let c = 0; c < N; c++) {
        if (inFinder(r, c)) {
          // 7x7 finder pattern: border ring + 3x3 center.
          const fr = r < 7 ? r : r - (N - 7);
          const fc = c < 7 ? c : c - (N - 7);
          const ring = fr === 0 || fr === 6 || fc === 0 || fc === 6;
          const center = fr >= 2 && fr <= 4 && fc >= 2 && fc <= 4;
          row.push(ring || center);
        } else {
          row.push(rng() > 0.5);
        }
      }
      grid.push(row);
    }
    return grid;
  }, [seed]);

  return (
    <svg
      viewBox={`0 0 ${N} ${N}`}
      className={className}
      shapeRendering="crispEdges"
      role="img"
      aria-label="Claim QR code"
    >
      <rect width={N} height={N} fill="#ffffff" />
      {cells.flatMap((row, r) =>
        row.map(
          (on, c) =>
            on && (
              <rect
                key={`${r}-${c}`}
                x={c}
                y={r}
                width={1}
                height={1}
                fill="#2b2220"
              />
            ),
        ),
      )}
    </svg>
  );
};

export default FakeQR;
